import type { Contact } from '@/api/types';

/**
 * "Is this address already a contact?" — decided here, React-free.
 *
 * Three answers, and the order between them is the whole point:
 *
 *  - **`contact`** — one of the card's addresses *is* this address. Comparison
 *    is case-insensitive and trimmed because an address is case-insensitive in
 *    its domain and, in every mail system anyone actually runs, in its local
 *    part too; a header that says `Finance@Example.test` and a card that says
 *    `finance@example.test` are the same person, and offering to add them a
 *    second time is how duplicate cards get made.
 *  - **`name`** — no card has the address, but one has exactly this display
 *    name. That is the "they got a new address" case, and it is an *offer*,
 *    never an automatic write: the user confirms it in the editor.
 *  - **`none`** — neither.
 *
 * Both comparisons are **exact after trim and case-folding**, never substring.
 * The server's search is a substring search (that is what makes it a useful
 * autocomplete), so the candidate list arriving here contains near-misses on
 * purpose; treating `notfinance@example.test` as `finance@example.test`
 * because one contains the other would silently attach a message's sender to
 * a stranger's card.
 *
 * An empty display name never matches. Plenty of cards are nameless, and a
 * rule that let `''` equal `''` would offer to bolt every anonymous address
 * onto the first nameless card in the book.
 */

export type AddressMatch =
  | { kind: 'contact'; contact: Contact }
  | { kind: 'name'; contact: Contact }
  | { kind: 'none' };

function fold(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** True when `contact` already carries `email`, whatever its case or type. */
export function hasAddress(contact: Contact, email: string): boolean {
  const needle = fold(email);
  if (needle === '') return false;
  return contact.emails.some((entry) => fold(entry.value) === needle);
}

export function contactForAddress(
  candidates: readonly Contact[],
  address: { name?: string | undefined; email: string },
): AddressMatch {
  const email = fold(address.email);
  if (email === '') return { kind: 'none' };

  const owner = candidates.find((candidate) => hasAddress(candidate, email));
  if (owner !== undefined) return { kind: 'contact', contact: owner };

  const name = fold(address.name);
  if (name === '') return { kind: 'none' };
  const sameName = candidates.find((candidate) => fold(candidate.displayName) === name);
  return sameName !== undefined ? { kind: 'name', contact: sameName } : { kind: 'none' };
}

/**
 * The same card with one more address on it, for the editor to open.
 *
 * The new address is appended (never made primary, never reordered) and the
 * ETag is carried through untouched, so the save is still the conflict-safe
 * `If-Match` write the editor always does. Returns the card unchanged when it
 * already has the address — the menu should not have offered this, and
 * returning a mutated copy anyway would write a duplicate row into the vCard.
 */
export function withAddress(contact: Contact, email: string): Contact {
  const value = email.trim();
  if (value === '' || hasAddress(contact, value)) return contact;
  return { ...contact, emails: [...contact.emails, { type: 'other', value }] };
}
